import { useEffect, useState } from "react";
import { Progress } from "antd";
import ByteUtils from "../../util/ByteUtils";
import businessBackgroundImage from "./images/business-background.jpg";
import databaseBackgroundImage from "./images/database-background.jpg";
import deviceBackgroundImage from "./images/device-background.jpg";
import DeviceMap from "../../components/ui-components/device-map/DeviceMap";
import { DashboardBody, DashboardHeader, DashboardWrapper, StatisticsItem, StatisticsItemContent, StatisticsTitle, StatisticsContent, TenantDatabaseStatisticsWrapper, TenantDeviceMapWrapper, Statistics, StatisticsValue, StatisticsText, StatisticsDesc, TenantDatabaseStatisticsItem, TenantNameAndDatabaseSize, TenantDatabaseTenantName, TenantDatabaseSizeText, TenantDeviceStatisticsWrapper, TenantDeviceStatisticsTitle, TenantDeviceStatisticsContent, TenantDeviceStatisticsItem, TenantNameAndDeviceCount, TenantDeviceTenantName, TenantDeviceCountText, TenantDatabaseStatisticsTitle, TenantDatabaseStatisticsContent } from "./Dashboard.styled";
import TenantApi from "../../api/TenantApi";
import DashboardApi from "../../api/DashboardApi";

interface DashboardProps {

}

const Dashboard: React.FC<DashboardProps> = (props)=> {

    let dashboardApi = new DashboardApi();
    let tenantApi = new TenantApi();

    // state
    let [tenantViews, setTenantViews] = useState<any>([]);
    let [overview, setOverview] = useState<any>({
        tenantStatistics: {
            total: 0,
            enabled: 0,
            disabled: 0,
            expired: 0,
        },
        tenantDatabaseStatistics: {
            totalSize: 0,
            dataSize: 0,
            indexSize: 0,
        },
        tenantDeviceStatistics: {
            total: 0,
            online: 0,
            offline: 0,
        },
    });

    // functions
    let formatDatabaseSize = (size)=> {
        return ByteUtils.formatBytes(size);
    };

    let formatDataSizeGB = (size)=> {
        return ByteUtils.bytesToGB(size);
    };

    let formatDatabasePercent = (size)=> {
        let maxTenantDatabaseSize = 0;
        for(let tenantView of tenantViews) {
            if (Number(tenantView.tenantDatabaseStatistics.totalSize) > maxTenantDatabaseSize) {
                maxTenantDatabaseSize = Number(tenantView.tenantDatabaseStatistics.totalSize);
            }
        }
        if (maxTenantDatabaseSize == 0) {
            return 100;
        }
        return (Number(size) / maxTenantDatabaseSize) * 100;
    };

    let formatDatabaseDataPercent = (tenantDatabaseStatistics: any) => {
        return formatDatabasePercent(tenantDatabaseStatistics.totalSize) * (Number(tenantDatabaseStatistics.dataSize) / Number(tenantDatabaseStatistics.totalSize));
    };
    
    let formatDeviceCountPercent = (count)=> {
        let maxTenantDeviceCount = 0;
        for(let tenantView of tenantViews) {
            if (tenantView.tenantNodeStatistics.total > maxTenantDeviceCount) {
                maxTenantDeviceCount = tenantView.tenantNodeStatistics.total;
            }
        }
        if (maxTenantDeviceCount == 0) {
            return 100;
        }
        return (count / maxTenantDeviceCount) * 100;
    };

    let formatDeviceOnlineCountPercent = (tenantNodeStatistics: any)=> {
        return formatDeviceCountPercent(tenantNodeStatistics.total) * (tenantNodeStatistics.online / tenantNodeStatistics.total);
    };

    let getPercent = (val, total)=> {
        if (total == 0) {
            return 100;
        }
        return (val / total) * 100;
    };

    let loadTenantViews = async ()=> {
        let tenantVies = (await tenantApi.getAllViews()).data.data;
        setTenantViews(tenantVies);
    };

    let loadOverview = async ()=> {
        let overview = (await dashboardApi.getSystemOverview()).data.data;
        setOverview(overview);
    };

    // effect
    useEffect(()=> {
        loadTenantViews();
        loadOverview();
    }, []);

    // render
    return (
        <DashboardWrapper>
            <DashboardHeader>
                <StatisticsItem style={{backgroundImage: `url(${businessBackgroundImage})`}}>
                    <StatisticsItemContent>
                        <StatisticsTitle>租户</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{overview.tenantStatistics.total}</StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{overview.tenantStatistics.enabled} 正常</StatisticsDesc>
                                        <StatisticsDesc>{overview.tenantStatistics.disabled} 禁用</StatisticsDesc>
                                        <StatisticsDesc>{overview.tenantStatistics.expired} 过期</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress 
                                    size="small" 
                                    percent={getPercent(overview.tenantStatistics.enabled + overview.tenantStatistics.disabled, overview.tenantStatistics.total)} 
                                    strokeColor="rgb(249, 125, 90)" 
                                    success={{percent: getPercent(overview.tenantStatistics.enabled, overview.tenantStatistics.total), strokeColor: 'rgb(28, 178, 115)'}} 
                                    showInfo={false} />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>

                <StatisticsItem style={{backgroundImage: `url(${databaseBackgroundImage})`}}>
                    <StatisticsItemContent>
                        <StatisticsTitle>数据库</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{formatDataSizeGB(overview.tenantDatabaseStatistics.totalSize).toFixed(2)}<span style={{fontSize: '12px'}}>GB</span></StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{formatDataSizeGB(overview.tenantDatabaseStatistics.dataSize).toFixed(2)}GB 数据</StatisticsDesc>
                                        <StatisticsDesc>{formatDataSizeGB(overview.tenantDatabaseStatistics.indexSize).toFixed(2)}GB 索引</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress 
                                    size="small" 
                                    percent={100} 
                                    strokeColor="rgb(249, 125, 90)" 
                                    success={{percent: getPercent(overview.tenantDatabaseStatistics.dataSize, overview.tenantDatabaseStatistics.totalSize), strokeColor: 'rgb(28, 178, 115)'}} 
                                    showInfo={false} 
                                    />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>

                <StatisticsItem style={{backgroundImage: `url(${deviceBackgroundImage})`}}>
                    <StatisticsItemContent>
                        <StatisticsTitle>设备数</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{overview.tenantDeviceStatistics.total} <span style={{fontSize: '12px'}}></span></StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{overview.tenantDeviceStatistics.online} 在线</StatisticsDesc>
                                        <StatisticsDesc>{overview.tenantDeviceStatistics.offline} 离线</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress 
                                    size="small" 
                                    percent={100} 
                                    strokeColor="rgb(249, 125, 90)" 
                                    success={{percent: getPercent(overview.tenantDeviceStatistics.online, overview.tenantDeviceStatistics.total), strokeColor: 'rgb(28, 178, 115)'}} 
                                    showInfo={false} 
                                    />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>
            </DashboardHeader>

            <DashboardBody>
                <TenantDeviceMapWrapper>
                    <DeviceMap />
                </TenantDeviceMapWrapper>

                <TenantDeviceStatisticsWrapper>
                    <TenantDeviceStatisticsTitle>租户设备统计</TenantDeviceStatisticsTitle>
                    <TenantDeviceStatisticsContent>
                    {
                        tenantViews.sort((a, b)=> b.tenantNodeStatistics.total - a.tenantNodeStatistics.total).map(tenantView  => {
                            return <TenantDeviceStatisticsItem key={tenantView.tenant.id}>
                                <TenantNameAndDeviceCount>                                    
                                    <TenantDeviceTenantName>{tenantView.tenant.name}</TenantDeviceTenantName>
                                    <Progress
                                        size="small"
                                        status="active"
                                        showInfo={true}
                                        percentPosition={{ align: 'end', type: 'outer' }}
                                        percent={Math.round(formatDeviceCountPercent(tenantView.tenantNodeStatistics.total))} 
                                        strokeColor="rgb(249, 125, 90)" 
                                        success={{percent: formatDeviceOnlineCountPercent(tenantView.tenantNodeStatistics), strokeColor: 'rgb(28, 178, 115)'}} 
                                        format={(percent, successPercent)=> {
                                            return <TenantDeviceCountText>{`${tenantView.tenantNodeStatistics.total}台`}</TenantDeviceCountText>;
                                        }}
                                    />
                                </TenantNameAndDeviceCount>
                            </TenantDeviceStatisticsItem>
                        })
                    }
                    </TenantDeviceStatisticsContent>
                </TenantDeviceStatisticsWrapper>

                <TenantDatabaseStatisticsWrapper>
                    <TenantDatabaseStatisticsTitle>租户数据库统计</TenantDatabaseStatisticsTitle>
                    <TenantDatabaseStatisticsContent>
                    {
                        tenantViews.sort((a, b)=> b.tenantDatabaseStatistics.totalSize - a.tenantDatabaseStatistics.totalSize).map(tenantView  => {
                            return <TenantDatabaseStatisticsItem key={tenantView.tenant.id}>
                                <TenantNameAndDatabaseSize>                                    
                                    <TenantDatabaseTenantName>{tenantView.tenant.name}</TenantDatabaseTenantName>
                                    <Progress
                                        size="small"
                                        status="active"
                                        showInfo={true}
                                        percentPosition={{ align: 'end', type: 'outer' }}
                                        percent={Math.round(formatDatabasePercent(tenantView.tenantDatabaseStatistics.totalSize))} 
                                        strokeColor="rgb(249, 125, 90)" 
                                        success={{percent: formatDatabaseDataPercent(tenantView.tenantDatabaseStatistics), strokeColor: 'rgb(28, 178, 115)'}} 
                                        format={(percent, successPercent)=> {
                                            return <TenantDatabaseSizeText>{`${formatDatabaseSize(tenantView.tenantDatabaseStatistics.totalSize)}`}</TenantDatabaseSizeText>;
                                        }}
                                    />
                                </TenantNameAndDatabaseSize>
                            </TenantDatabaseStatisticsItem>
                        })
                    }
                    </TenantDatabaseStatisticsContent>
                </TenantDatabaseStatisticsWrapper>
            </DashboardBody>
        </DashboardWrapper>
    );
}

export default Dashboard;
